import { createSelector, createStructuredSelector } from 'reselect';
import {
  loanPurposeOptionsGem,
  loanPurposeOptionsLatitude,
  loans,
  repaymentsPerMonthOptions
} from '../../data/js';
import { monthsToYearMonthString } from '../../utils';
import {
  getAmortisationSchedule,
  getRepayment,
  getPeriodicRate
} from '../../utils/loanCalculatorUtil';
import { LOAN_PAY_FREQUENCY } from '../../utils/constants';

const calcVersion = (state, props) => props.calcVersion;
const loanAmount = state => state.loanAmount;
const loanMinimum = state => state.loanMinimum;
const loanDurationYears = state => state.loanDurationYears;
const repaymentsPerMonth = state => state.repaymentsPerMonth;
const selectedLoanPurpose = state => state.selectedLoanPurpose;
const isMortgageHolder = state => state.isMortgageHolder;
const isExistingLoanCustomer = state => state.isExistingLoanCustomer;
const extraRepaymentAmount = state => state.extraRepaymentAmount;
const extraRepaymentsPerMonth = state => state.extraRepaymentsPerMonth;

const interestRate = state => state.interestRate;
const repaymentsYears = state => state.repaymentsYears;
const repaymentPeriod = state => state.repaymentPeriod;

// Fees which apply to calculator
const establishmentFeeValue = 295;
const ppsrRegFee = 6;
const ppsrSearchFee = 2;
const creditSearchFee = 2.84;
const ACCOUNT_ADMIN_FEE = 10;
const totalFees =
  establishmentFeeValue + ppsrRegFee + ppsrSearchFee + creditSearchFee;

export const loanPurposeOptions = createSelector(calcVersion, calcVersion => {
  return calcVersion === 'Latitude'
    ? loanPurposeOptionsLatitude
    : calcVersion === 'Gem'
    ? loanPurposeOptionsGem
    : [{}];
});

export const loanDetails = createSelector(
  calcVersion,
  selectedLoanPurpose,
  loanAmount,
  isMortgageHolder,
  isExistingLoanCustomer,
  (calcVersion, selectedLoanPurpose, loanAmount) => {
    let loanName;
    if (calcVersion === 'Latitude') {
      if (
        selectedLoanPurpose === 'Car purchase' ||
        selectedLoanPurpose === 'Other vehicle purchase'
      ) {
        if (loanAmount < 5000) {
          loanName = 'Latitude Personal Loan';
        } else {
          loanName = 'Latitude Car and Motor Loan';
        }
      } else {
        loanName = 'Latitude Personal Loan';
      }
    } else if (calcVersion === 'Gem') {
      if (selectedLoanPurpose === 'Auto Purchase' && loanAmount >= 6000) {
        loanName = 'Gem Car Loan';
      } else {
        loanName = 'Gem Personal Loan';
      }
    }
    return loans.find(loan => loan.name === loanName);
  }
);

export const loanName = createSelector(
  loanDetails,
  loanDetails => loanDetails.name
);

export const loanRate = createSelector(
  loanDetails,
  loanDetails => loanDetails.rate
);

export const loanComparisonRate = createSelector(
  loanDetails,
  loanDetails => loanDetails.comparisonRate
);

export const loanComparisonRateText = createSelector(
  loanDetails,
  loanDetails => loanDetails.comparisonRateText
);

export const loanMonthlyFee = createSelector(loanDetails, loanDetails => {
  return loanDetails.loanMonthlyFee;
});

export const disclaimer = createSelector(
  loanDetails,
  loanDetails => loanDetails.disclaimer
);

export const shouldShowResults = createSelector(
  loanAmount,
  loanMinimum,
  selectedLoanPurpose,
  loanDurationYears,
  repaymentsPerMonth,
  calcVersion,
  isExistingLoanCustomer,
  isMortgageHolder,
  (
    loanAmount,
    loanMinimum,
    selectedLoanPurpose,
    loanDurationYears,
    repaymentsPerMonth,
    calcVersion,
    isExistingLoanCustomer,
    isMortgageHolder
  ) => {
    return (
      loanAmount >= loanMinimum &&
      selectedLoanPurpose &&
      loanDurationYears &&
      repaymentsPerMonth &&
      (calcVersion === 'Latitude' && selectedLoanPurpose !== 'Car purchase'
        ? loanAmount < 20000 ||
          (isExistingLoanCustomer !== undefined &&
            isMortgageHolder !== undefined)
        : true)
    );
  }
);

// If ever set to true, always return true (openable, not closable)
// Note: sharing of this selector file between components will created shared access to this variable
// (but should never packaged together and put on the same page anyway)
let shouldShowResultsMemo;
export const shouldShowResultsPanel = createSelector(
  shouldShowResults,
  shouldShowResults => {
    if (shouldShowResults) {
      shouldShowResultsMemo = true;
    }
    return (shouldShowResultsMemo || shouldShowResults) && true;
  }
);

export const shouldShowMortgageQuestion = createSelector(
  loanAmount,
  calcVersion,
  selectedLoanPurpose,
  (loanAmount, calcVersion, selectedLoanPurpose) => {
    return (
      loanAmount >= 20000 &&
      calcVersion === 'Latitude' &&
      selectedLoanPurpose !== 'Car purchase'
    );
  }
);

export const shouldShowExistingCustomerQuestion = createSelector(
  shouldShowMortgageQuestion,
  shouldShowMortgageQuestion => {
    return shouldShowMortgageQuestion;
  }
);

export const loanDurationYearsOptions = createSelector(
  calcVersion,
  selectedLoanPurpose,
  (calcVersion, selectedLoanPurpose) => {
    if (calcVersion === 'Gem') {
      return [0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7].map(i => {
        return { text: monthsToYearMonthString(i * 12), value: i };
      });
    }

    if (calcVersion === 'Latitude') {
      if (
        selectedLoanPurpose === 'Car purchase' ||
        selectedLoanPurpose === 'Other vehicle purchase'
      ) {
        return [
          { text: 1, value: 1 },
          { text: 2, value: 2 },
          { text: 3, value: 3 },
          { text: 4, value: 4 },
          { text: 5, value: 5 },
          { text: 6, value: 6 },
          { text: 7, value: 7 }
        ];
      } else {
        return [
          { text: 2, value: 2 },
          { text: 3, value: 3 },
          { text: 4, value: 4 },
          { text: 5, value: 5 },
          { text: 6, value: 6 },
          { text: 7, value: 7 }
        ];
      }
    }
  }
);

export const establishmentFee = createSelector(
  loanAmount,
  loanDetails,
  (loanAmount, loanDetails) => {
    return (
      loanDetails &&
      loanDetails.getEstablishmentFee &&
      loanDetails.getEstablishmentFee(loanAmount)
    );
  }
);

export const repaymentAsMonthly = createSelector(
  loanAmount,
  loanRate,
  loanDurationYears,
  establishmentFee,
  loanMonthlyFee,
  (
    loanAmount,
    loanRate,
    loanDurationYears,
    establishmentFee,
    loanMonthlyFee
  ) => {
    return getRepayment(
      loanAmount + establishmentFee,
      loanRate,
      loanDurationYears,
      LOAN_PAY_FREQUENCY.MONTHLY,
      loanMonthlyFee,
      false
    );
  }
);

export const repaymentPerInterval = createSelector(
  repaymentAsMonthly,
  repaymentsPerMonth,
  (repaymentAsMonthly, repaymentsPerMonth) => {
    return repaymentAsMonthly / repaymentsPerMonth;
  }
);

export const repaymentsPerMonthWording = createSelector(
  repaymentsPerMonth,
  repaymentsPerMonth => {
    var selectedFrequency = repaymentsPerMonthOptions.find(
      item => item.value === repaymentsPerMonth
    );
    return selectedFrequency ? selectedFrequency.text : '';
  }
);

export const calculatedInterest = createSelector(
  loanAmount,
  repaymentAsMonthly,
  loanDurationYears,
  (loanAmount, repaymentAsMonthly, loanDurationYears) => {
    let numPayments = loanDurationYears * 12;
    return repaymentAsMonthly * numPayments - loanAmount;
  }
);

export const extraRepaymentAsMonthly = createSelector(
  extraRepaymentAmount,
  extraRepaymentsPerMonth,
  (extraRepaymentAmount, extraRepaymentsPerMonth) => {
    return extraRepaymentAmount * extraRepaymentsPerMonth;
  }
);

export const baseAmortisationSchedule = createSelector(
  loanAmount,
  loanRate,
  repaymentAsMonthly,
  (loanAmount, loanRate, repaymentAsMonthly) => {
    return getAmortisationSchedule(loanAmount, loanRate, repaymentAsMonthly);
  }
);

export const extraRepaymentsAmortisationSchedule = createSelector(
  loanAmount,
  loanRate,
  repaymentAsMonthly,
  extraRepaymentAsMonthly,
  (loanAmount, loanRate, repaymentAsMonthly, extraRepaymentAsMonthly) => {
    let repaymentWithExtra = extraRepaymentAsMonthly + repaymentAsMonthly;
    return getAmortisationSchedule(loanAmount, loanRate, repaymentWithExtra);
  }
);

export const extraRepaymentsTimeSaved = createSelector(
  extraRepaymentAsMonthly,
  loanDurationYears,
  baseAmortisationSchedule,
  extraRepaymentsAmortisationSchedule,
  (
    extraRepaymentAsMonthly,
    loanDurationYears,
    baseAmortisationSchedule,
    extraRepaymentsAmortisationSchedule
  ) => {
    if (!extraRepaymentAsMonthly) return 0;
    let timeSaved =
      loanDurationYears * 12 - extraRepaymentsAmortisationSchedule.length;
    return Math.max(0, timeSaved);
  }
);

export const extraRepaymentsInterestSaved = createSelector(
  extraRepaymentAsMonthly,
  baseAmortisationSchedule,
  extraRepaymentsAmortisationSchedule,
  (
    extraRepaymentAsMonthly,
    baseAmortisationSchedule,
    extraRepaymentsAmortisationSchedule
  ) => {
    if (!extraRepaymentAsMonthly) return 0;
    let baseInterest = baseAmortisationSchedule.reduce(
      (total, value) => total + value.interest,
      0
    );
    let newInterest = extraRepaymentsAmortisationSchedule.reduce(
      (total, value) => total + value.interest,
      0
    );
    let interestSaved = baseInterest - newInterest;
    return Math.max(0, interestSaved);
  }
);

// TODO maybe: Refactor selectedLoanPurpose (text value) into selectedLoanPurpose (full object)

export const findOutMoreAddress = createSelector(
  loanPurposeOptions,
  selectedLoanPurpose,
  (loanPurposeOptions, selectedLoanPurpose) => {
    let loanPurposeDetails = loanPurposeOptions.find(
      loanPurpose => loanPurpose.value === selectedLoanPurpose
    );
    return loanPurposeDetails ? loanPurposeDetails.findOutMoreAddress : '';
  }
);

export const applyNowUrl = createSelector(
  loanDurationYears,
  selectedLoanPurpose,
  calcVersion,
  loanName,
  loanAmount,
  (
    loanDurationYears,
    selectedLoanPurpose,
    calcVersion,
    loanName,
    loanAmount
  ) => {
    let loanDurationForUrl = monthsToYearMonthString(loanDurationYears * 12)
      .split(' ')
      .join('+');
    let loanPurposeForUrl = selectedLoanPurpose.split(' ').join('+');
    if (loanName !== 'Latitude Car and Motor Loan') {
      return `https://lfs-apply.mypersonalloanapp.com/${calcVersion.toLowerCase()}?amount=${loanAmount}&term=${loanDurationForUrl}&purpose=${loanPurposeForUrl}`;
    }
  }
);

export const calculateMonthlyInterestRate = createSelector(
  interestRate,
  interestRate => {
    return getPeriodicRate(interestRate);
  }
);

export const calculateMotorRepayment = createSelector(
  loanAmount,
  repaymentsYears,
  repaymentPeriod,
  interestRate,
  (loanAmount, repaymentsYears, repaymentPeriod, interestRate) => {
    if (!loanAmount || !repaymentsYears || !interestRate || !repaymentPeriod) {
      return 0;
    }

    let payFrequency;
    if (repaymentPeriod === 'weekly') {
      payFrequency = LOAN_PAY_FREQUENCY.WEEKLY;
    } else if (repaymentPeriod === 'fortnightly') {
      payFrequency = LOAN_PAY_FREQUENCY.FORTNIGHTLY;
    } else {
      payFrequency = LOAN_PAY_FREQUENCY.MONTHLY;
    }

    return getRepayment(
      loanAmount + totalFees,
      interestRate,
      repaymentsYears,
      payFrequency,
      ACCOUNT_ADMIN_FEE,
      true
    );
  }
);

export const calculateTotalInterestPaid = createSelector(
  loanAmount,
  repaymentsYears,
  repaymentPeriod,
  calculateMonthlyInterestRate,
  (
    loanAmount,
    repaymentsYears,
    repaymentPeriod,
    calculateMonthlyInterestRate
  ) => {
    let monthlyRepayment = 0;
    if (!loanAmount || !repaymentsYears || !calculateMonthlyInterestRate) {
      return 0;
    }

    // Calculate total amount paid to use for total interest paid value calculation
    const monthlyInterestRate = calculateMonthlyInterestRate;
    monthlyRepayment =
      (monthlyInterestRate * (loanAmount + totalFees)) /
      (1 - (1 + monthlyInterestRate) ** (-repaymentsYears * 12));

    const totalAmountPaidWithFees =
      Math.ceil(monthlyRepayment + ACCOUNT_ADMIN_FEE) * (repaymentsYears * 12);
    const loanAmountWithFees = loanAmount + totalFees;

    if (repaymentPeriod === 'monthly') {
      return Math.ceil(
        totalAmountPaidWithFees -
          loanAmountWithFees -
          ACCOUNT_ADMIN_FEE * (repaymentsYears * 12)
      );
    }
  }
);

export const calculateTotalAmountPaid = createSelector(
  loanAmount,
  repaymentsYears,
  repaymentPeriod,
  calculateMonthlyInterestRate,
  (
    loanAmount,
    repaymentsYears,
    repaymentPeriod,
    calculateMonthlyInterestRate
  ) => {
    let monthlyRepayment = 0;
    if (!loanAmount || !repaymentsYears || !calculateMonthlyInterestRate) {
      return monthlyRepayment;
    }

    // Calculate estimated monthly amount
    const monthlyInterestRate = calculateMonthlyInterestRate;
    monthlyRepayment =
      (monthlyInterestRate * (loanAmount + totalFees)) /
      (1 - (1 + monthlyInterestRate) ** (-repaymentsYears * 12));

    // Output total amount paid
    if (repaymentPeriod === 'monthly') {
      return (
        Math.ceil(monthlyRepayment + ACCOUNT_ADMIN_FEE) * (repaymentsYears * 12)
      );
    }
  }
);

export const structuredSelector = createStructuredSelector({
  loanPurposeOptions,
  loanDetails,
  loanName,
  loanRate,
  loanComparisonRate,
  loanComparisonRateText,
  loanMonthlyFee,
  establishmentFee,
  shouldShowResults,
  shouldShowResultsPanel,
  shouldShowMortgageQuestion,
  shouldShowExistingCustomerQuestion,
  loanDurationYearsOptions,
  repaymentAsMonthly,
  repaymentPerInterval,
  repaymentsPerMonthWording,
  calculatedInterest,
  extraRepaymentAsMonthly,
  extraRepaymentsTimeSaved,
  extraRepaymentsInterestSaved,
  extraRepaymentsAmortisationSchedule,
  findOutMoreAddress,
  applyNowUrl,
  disclaimer
});

export const motorLoanSelector = createStructuredSelector({
  calculateMonthlyInterestRate,
  calculateMotorRepayment,
  calculateTotalInterestPaid,
  calculateTotalAmountPaid
});
